"use client"

import styled from "@emotion/styled"
import { useTranslations } from "next-intl"
import { useEffect, useMemo, useState } from "react"
import { extractProperty, PortItem } from "utils"
import PortContainer from "../PortContainer"
import {
  isEventsConferences,
  isMeetingRoom,
} from "../PortFacilitiesInnerPage/constants"
import BookingCard from "./BookingCard"
import BookingDrawer from "./BookingDrawer"
import CancellationPolicy from "./CancellationPolicy"
import ContactCard from "./ContactCard"
import ContactDrawer from "./ContactDrawer"
import DirectionsSection from "./DirectionsSection"
import ImportantPoints from "./ImportantPoints"
import RoomHeader from "./RoomHeader"
import ServicesGrid from "./ServicesGrid"
import StickyBottomBar from "./StickyBottomBar"
import { useHostExtras } from "./useHostExtras"
import { useHostPrice } from "./useHostPrice"
import { filterTimeSlotsToRange, getShouldDisableDate, useHostResource } from "./useHostResource"
import { getSlotStatus, useHostSlots } from "./useHostSlots"

/** Hourly time slots for meeting room booking (HOST API uses 60-min intervals) */
const HOURLY_SLOTS: string[] = (() => {
  const slots: string[] = []
  for (let h = 7; h <= 22; h++) {
    slots.push(`${h.toString().padStart(2, "0")}:00`)
  }
  return slots
})()

const StyledWrapper = styled.section`
  display: flex;
  flex-direction: column;
  width: 100%;
  background: white;

  ${({ theme }) => theme.breakpoints.up("md")} {
    flex-direction: row;
    gap: 40px;
  }
`

const StyledContentColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`

const StyledSidebarColumn = styled.div`
  display: none;

  ${({ theme }) => theme.breakpoints.up("md")} {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    position: sticky;
    top: 102px;
    align-self: flex-start;
    height: fit-content;
  }
`

export type BookingSearchParams = {
  date?: string
  startTime?: string
  endTime?: string
  numberOfPeople?: string
}

type PortMeetingRoomProductProps = {
  data: any
  ports?: PortItem[]
  pageData?: any
  searchParams?: BookingSearchParams
  companyId?: number
}

function PortMeetingRoomProduct({
  data,
  ports,
  pageData,
  searchParams,
  companyId,
}: PortMeetingRoomProductProps) {
  const t = useTranslations("portMeetingRoomProduct")
  const [drawerOpen, setDrawerOpen] = useState(false)

  const page = pageData?.page || null
  const facilityType = extractProperty(page, "facilityType") || ""
  const isMeetingRoomType = isMeetingRoom(facilityType)
  const isEventsType = isEventsConferences(facilityType)

  // HOST identifiers
  const rawHostResourceId = extractProperty(page, "hostResourceId")
  const hostResourceId = rawHostResourceId ? Number(rawHostResourceId) : undefined
  const pageUrl = page?.url || ""
  const parentPort = useMemo(() => {
    if (!ports || !pageUrl) return null
    return ports.find((p) => pageUrl.includes(p.url)) || null
  }, [ports, pageUrl])
  const hostLocationName = parentPort?.hostLocationName || ""

  // Shared booking state (lifted so price hook can access)
  const [selectedDate, setSelectedDate] = useState(searchParams?.date || "")
  const [selectedStartTime, setSelectedStartTime] = useState(searchParams?.startTime || "")
  const [selectedEndTime, setSelectedEndTime] = useState(searchParams?.endTime || "")
  const [selectedExtras, setSelectedExtras] = useState<Record<number, number>>({})

  // HOST data hooks
  const { resource: hostResource } = useHostResource(isMeetingRoomType ? hostResourceId : undefined)
  const { extras } = useHostExtras(isMeetingRoomType ? hostLocationName : "")
  const slotDates = useMemo(() => (selectedDate ? [selectedDate] : []), [selectedDate])
  const { availability } = useHostSlots(isMeetingRoomType ? hostResourceId : undefined, slotDates)
  const shouldDisableDate = useMemo(
    () => getShouldDisableDate(hostResource?.availableDays),
    [hostResource?.availableDays],
  )

  const availableTimeSlots = useMemo(
    () =>
      filterTimeSlotsToRange(HOURLY_SLOTS, hostResource?.availableFrom, hostResource?.availableTo),
    [hostResource?.availableFrom, hostResource?.availableTo],
  )

  const busySlots = useMemo(() => {
    if (!selectedDate || availability.length === 0) return []
    const dateAvail = availability.find((a) => a.date === selectedDate)
    const { busy } = getSlotStatus(dateAvail, availableTimeSlots)
    return busy
  }, [selectedDate, availability, availableTimeSlots])

  const { priceData, loading: priceLoading } = useHostPrice({
    hostResourceId: isMeetingRoomType ? hostResourceId : undefined,
    startTime: selectedStartTime,
    endTime: selectedEndTime,
    selectedExtras,
  })

  // Block CMS data
  const servicesBlackTitle = extractProperty(data, "servicesBlackTitle") || ""
  const servicesBlueTitle = extractProperty(data, "servicesBlueTitle") || ""
  const servicesItems = extractProperty(data, "servicesItems") || []
  const importantPointsBlackTitle = extractProperty(data, "importantPointsBlackTitle") || ""
  const importantPointsBlueTitle = extractProperty(data, "importantPointsBlueTitle") || ""
  const importantPointsIntro = extractProperty(data, "importantPointsIntro") || ""
  const importantPointsList = extractProperty(data, "importantPointsList") || []
  const directionsBlueTitle = extractProperty(data, "directionsBlueTitle") || ""
  const directionsBlackTitle = extractProperty(data, "directionsBlackTitle") || ""
  const directionsText = extractProperty(data, "directionsText") || ""
  const parkingLinks = extractProperty(data, "directionsParkingLink")
  const parkingLink = parkingLinks?.[0]
    ? { name: parkingLinks[0].name || "", url: parkingLinks[0].url || "" }
    : null

  const cancellationBlueTitle = extractProperty(data, "cancellationBlueTitle") || ""
  const cancellationBlackTitle = extractProperty(data, "cancellationBlackTitle") || ""
  const cancellationText = extractProperty(data, "cancellationText") || ""

  // Thank you page CMS data — store in sessionStorage so PaymentPage can access it
  const thankYouContactText = extractProperty(data, "thankYouContactText") || ""
  const thankYouPhone = extractProperty(data, "thankYouPhone") || ""
  const thankYouEmail = extractProperty(data, "thankYouEmail") || ""
  const thankYouWhatsApp = extractProperty(data, "thankYouWhatsApp") || ""

  useEffect(() => {
    if (
      isMeetingRoomType &&
      (thankYouContactText || thankYouPhone || thankYouEmail || thankYouWhatsApp)
    ) {
      sessionStorage.setItem(
        "thankYouContact",
        JSON.stringify({ thankYouContactText, thankYouPhone, thankYouEmail, thankYouWhatsApp }),
      )
    }
  }, [isMeetingRoomType, thankYouContactText, thankYouPhone, thankYouEmail, thankYouWhatsApp])

  if (!data) return null

  // Facility data (from parent PortFacilityPage)
  const roomName = extractProperty(page, "facilityName") || ""
  const address = extractProperty(page, "address") || ""
  const capacity =
    extractProperty(page, "parkingName") || extractProperty(page, "amountOfPeople") || ""
  const description =
    extractProperty(page, "description") || extractProperty(page, "deescription") || ""
  const price = extractProperty(page, "price") || ""

  // Only show HOST live price, no fallback
  const displayPrice = priceData ? `₪${priceData.order.finalPriceWithVat.toFixed(0)}` : ""

  return (
    <>
      <PortContainer paddingInline={20}>
        <StyledWrapper>
          <StyledContentColumn>
            <RoomHeader
              name={roomName}
              address={address}
              capacity={capacity}
              description={description}
            />
            <ServicesGrid
              blackTitle={servicesBlackTitle}
              blueTitle={servicesBlueTitle}
              items={servicesItems}
            />
            <ImportantPoints
              blackTitle={importantPointsBlackTitle}
              blueTitle={importantPointsBlueTitle}
              intro={importantPointsIntro}
              items={importantPointsList}
            />
            <DirectionsSection
              blueTitle={directionsBlueTitle}
              blackTitle={directionsBlackTitle}
              text={directionsText}
              parkingLink={parkingLink}
            />
            <CancellationPolicy
              blueTitle={cancellationBlueTitle}
              blackTitle={cancellationBlackTitle}
              text={cancellationText}
            />
          </StyledContentColumn>

          <StyledSidebarColumn>
            {isMeetingRoomType ? (
              <BookingCard
                price={displayPrice}
                searchParams={searchParams}
                extras={extras}
                busySlots={busySlots}
                selectedExtras={selectedExtras}
                onDateChange={setSelectedDate}
                onStartTimeChange={setSelectedStartTime}
                onEndTimeChange={setSelectedEndTime}
                onExtrasChange={setSelectedExtras}
                priceLoading={priceLoading}
                hostResourceId={hostResourceId}
                hostLocationName={hostLocationName}
                shouldDisableDate={shouldDisableDate}
                availableTimeSlots={availableTimeSlots}
              />
            ) : (
              <ContactCard
                data={data}
                companyId={companyId}
                showPrice={isEventsType}
                showDatePicker={isEventsType}
                price={price}
                companySectionLabel={isEventsType ? t("businessDetails") : undefined}
                mediaTitle={hostLocationName}
              />
            )}
          </StyledSidebarColumn>
        </StyledWrapper>
      </PortContainer>

      <StickyBottomBar
        onClick={() => setDrawerOpen(true)}
        label={isMeetingRoomType ? undefined : t("scheduleTour")}
      />
      {isMeetingRoomType ? (
        <BookingDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          price={displayPrice}
          searchParams={searchParams}
          extras={extras}
          busySlots={busySlots}
          selectedExtras={selectedExtras}
          onDateChange={setSelectedDate}
          onStartTimeChange={setSelectedStartTime}
          onEndTimeChange={setSelectedEndTime}
          onExtrasChange={setSelectedExtras}
          priceLoading={priceLoading}
          hostResourceId={hostResourceId}
          hostLocationName={hostLocationName}
          shouldDisableDate={shouldDisableDate}
          availableTimeSlots={availableTimeSlots}
        />
      ) : (
        <ContactDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          data={data}
          companyId={companyId}
          showPrice={isEventsType}
          showDatePicker={isEventsType}
          price={price}
          companySectionLabel={isEventsType ? t("businessDetails") : undefined}
        />
      )}
    </>
  )
}

export default PortMeetingRoomProduct
